import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  Empty,
  message,
  Popconfirm,
  Space,
  Spin,
  Tag,
  theme,
  Typography,
} from "antd";
import { RollbackOutlined } from "@ant-design/icons";
import { useAppDispatch } from "../../../app/hooks";
import { IEmailTemplateDetail } from "../../../interfaces/emailTemplate.interface";
import {
  discardEmailDraftThunk,
  getEmailTemplateThunk,
} from "../emailTemplatesSlice";
import { LOCALE_LABELS } from "../emailTemplatesHelpers";
import {
  BlockChange,
  blockSummary,
  diffTemplate,
  LocaleDiff,
} from "../templateDiff";

const { Text } = Typography;

const STATUS_META: Record<
  BlockChange,
  { color: string; label: string; sign: string }
> = {
  added: { color: "#237804", label: "Added", sign: "+" },
  removed: { color: "#a8071a", label: "Removed", sign: "−" },
  changed: { color: "#ad6800", label: "Changed", sign: "~" },
  unchanged: { color: "#8c8c8c", label: "Unchanged", sign: " " },
};

interface TemplateDiffDrawerProps {
  open: boolean;
  slug: string | null;
  onClose: () => void;
  onDiscarded: () => void;
}

const TemplateDiffDrawer: React.FC<TemplateDiffDrawerProps> = ({
  open,
  slug,
  onClose,
  onDiscarded,
}) => {
  const dispatch = useAppDispatch();
  const { token } = theme.useToken();
  const [detail, setDetail] = useState<IEmailTemplateDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  useEffect(() => {
    if (!open || !slug) return;
    setDetail(null);
    setLoading(true);
    dispatch(getEmailTemplateThunk(slug))
      .unwrap()
      .then(setDetail)
      .catch(() => message.error("Failed to load template."))
      .finally(() => setLoading(false));
  }, [open, slug, dispatch]);

  const diffs = useMemo(
    () => (detail ? diffTemplate(detail).filter((d) => d.changed) : []),
    [detail]
  );

  const handleDiscard = async () => {
    if (!slug) return;
    setDiscarding(true);
    try {
      await dispatch(discardEmailDraftThunk({ slug })).unwrap();
      message.success("Draft discarded — reverted to published.");
      onDiscarded();
    } catch {
      message.error("Failed to discard draft.");
    } finally {
      setDiscarding(false);
    }
  };

  const cellStyle: React.CSSProperties = {
    flex: 1,
    whiteSpace: "pre-wrap",
    fontSize: 13,
    padding: token.paddingSM,
    borderRadius: token.borderRadius,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: token.colorBgLayout,
  };

  const renderLocale = (diff: LocaleDiff) => (
    <div key={diff.locale} style={{ marginBottom: token.marginLG }}>
      <Text strong>{LOCALE_LABELS[diff.locale] ?? diff.locale}</Text>

      {diff.subjectChanged && (
        <div style={{ margin: `${token.marginXS}px 0` }}>
          <Tag color="gold">Subject changed</Tag>
          <div style={{ display: "flex", gap: token.marginXS, marginTop: 4 }}>
            <div style={cellStyle}>
              <Text type="secondary">Published</Text>
              <div>{diff.publishedSubject || "—"}</div>
            </div>
            <div style={cellStyle}>
              <Text type="secondary">Draft</Text>
              <div>{diff.draftSubject || "—"}</div>
            </div>
          </div>
        </div>
      )}

      {diff.rows
        .filter((row) => row.status !== "unchanged")
        .map((row, index) => {
          const meta = STATUS_META[row.status];
          return (
            <div key={index} style={{ marginTop: token.marginXS }}>
              <Tag color={meta.color}>{meta.label}</Tag>
              <div
                style={{ display: "flex", gap: token.marginXS, marginTop: 4 }}
              >
                <div style={cellStyle}>
                  <Text type="secondary">Published</Text>
                  <div>{blockSummary(row.published) || "—"}</div>
                </div>
                <div style={cellStyle}>
                  <Text type="secondary">Draft</Text>
                  <div>{blockSummary(row.draft) || "—"}</div>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="70%"
      title={slug ? `Pending changes: ${slug}` : "Pending changes"}
      extra={
        <Popconfirm
          title="Discard draft changes?"
          description="The draft is reverted to the currently published content."
          okText="Discard"
          okButtonProps={{ danger: true }}
          onConfirm={handleDiscard}
          disabled={!diffs.length}
        >
          <Button
            danger
            icon={<RollbackOutlined />}
            loading={discarding}
            disabled={!diffs.length}
          >
            Discard draft
          </Button>
        </Popconfirm>
      }
    >
      {loading ? (
        <Spin />
      ) : diffs.length ? (
        <Space direction="vertical" style={{ width: "100%" }}>
          {diffs.map(renderLocale)}
        </Space>
      ) : (
        <Empty description="No pending changes — draft matches published." />
      )}
    </Drawer>
  );
};

export default TemplateDiffDrawer;
